import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Building2,
  Home,
  Activity,
  FileText,
  Menu,
  X,
  LogOut,
  Shield,
  User,
  Building,
  Users,
  Briefcase,
  MapPin,
  Table,
  BarChart3,
  Plug,
  Compass
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

// Type definitions
interface UserType {
  id: string
  email: string
  first_name?: string
  last_name?: string
  role: string
}

// import { TagusAgent } from './TagusAgent' // Temporarily disabled

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { user, logout, isAdmin } = useAuth() as { user: UserType | null, logout: () => void, isAdmin: boolean }

  // Monitoring issues badge count — admin-only endpoint, skip the query for
  // analysts to avoid an unnecessary 403.
  const { data: issuesMeta } = useQuery({
    queryKey: ['monitoring-issues-meta'],
    queryFn: async () => {
      const response = await api.get('/automation/monitoring/issues/meta?dismissed=false')
      return response.data
    },
    refetchInterval: 300000, // 5 minutes
    staleTime: 60000,
    enabled: isAdmin,
  })

  // Developments waiting for approval — same pattern as the Monitoring badge.
  const { data: discoveryMeta } = useQuery({
    queryKey: ['discovery-meta-badge'],
    queryFn: async () => {
      const response = await api.get('/discovery/meta')
      return response.data
    },
    refetchInterval: 300000, // 5 minutes
    staleTime: 60000,
    enabled: isAdmin,
  })

  // Navigation items visible to every authenticated user (admin + analyst)
  const baseNavigationItems = [
    { name: 'Projects Map', href: '/projects-map', icon: MapPin },
    { name: 'Projects', href: '/projects', icon: Building2 },
    { name: 'Units', href: '/units', icon: Home },
    { name: 'Architects', href: '/architects', icon: Building },
    { name: 'Developers', href: '/developers', icon: Users },
    { name: 'Brokers', href: '/brokers', icon: Briefcase },
  ]

  // Admin-only navigation items (Analytics, Monitoring, Audit, User Management, MCP Access)
  const adminNavigationItems = [
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'Discovery', href: '/discovery', icon: Compass },
    { name: 'Monitoring', href: '/monitoring', icon: Activity },
    { name: 'Audit', href: '/audit', icon: FileText },
    { name: 'User Management', href: '/admin/users', icon: Shield },
    { name: 'MCP Access', href: '/admin/mcp', icon: Plug },
  ]

  const allNavigationItems = isAdmin
    ? [...baseNavigationItems, ...adminNavigationItems]
    : baseNavigationItems

  const isActiveRoute = (href: string) => {
    return location.pathname === href
  }
  // Breadcrumb segment component to handle data fetching
  const BreadcrumbSegment = ({ value, index, pathnames }: { value: string, index: number, pathnames: string[] }) => {
    const isId = !isNaN(Number(value))
    const type = index > 0 ? pathnames[index - 1] : ''
    
    // Determine query configuration key based on parent path
    const getQueryConfig = () => {
      if (!isId) return null
      
      switch (type) {
        case 'projects':
          return { key: 'project', endpoint: 'projects', labelField: 'name' }
        case 'units':
          return { key: 'unit', endpoint: 'units', labelField: 'unit_ref' } // Or 'fraction'
        case 'architects':
          return { key: 'architect', endpoint: 'architects', labelField: 'name' }
        case 'developers':
          return { key: 'developer', endpoint: 'developers', labelField: 'name' }
        case 'brokers':
          return { key: 'broker', endpoint: 'brokers', labelField: 'name' }
        default:
          return null
      }
    }

    const queryConfig = getQueryConfig()

    const { data: entity } = useQuery({
      queryKey: [queryConfig?.key, value],
      queryFn: async () => {
        if (!queryConfig) return null
        const res = await api.get(`/${queryConfig.endpoint}/${value}`)
        return res.data
      },
      enabled: !!queryConfig,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    })

    // Determine display label
    let label = value.replace(/-/g, ' ')
    label = label.charAt(0).toUpperCase() + label.slice(1)

    if (isId) {
      if (entity && queryConfig) {
        // Special handling for units to show fraction if available, otherwise ref
        if (queryConfig.key === 'unit') {
          label = entity.unit_ref // You can customize this, e.g. `Unit ${entity.unit_ref}`
        } else {
          label = entity[queryConfig.labelField] || `#${value}`
        }
      } else {
        label = `#${value}`
      }
    }

    const to = `/${pathnames.slice(0, index + 1).join('/')}`
    const isLast = index === pathnames.length - 1

    return (
      <React.Fragment key={to}>
        <li>
          <span className="text-secondary-300">/</span>
        </li>
        <li>
          {isLast ? (
            <span className="text-sm font-medium text-secondary-900" aria-current="page">
              {label}
            </span>
          ) : (
            <Link to={to} className="text-sm font-medium text-secondary-500 hover:text-secondary-900">
              {label}
            </Link>
          )}
        </li>
      </React.Fragment>
    )
  }

  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x)

    return (
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/" className="text-secondary-400 hover:text-secondary-500">
              <Home className="h-4 w-4" />
            </Link>
          </li>
          {pathnames.map((value, index) => (
             <BreadcrumbSegment key={index} value={value} index={index} pathnames={pathnames} />
          ))}
        </ol>
      </nav>
    )
  }

  return (
    <div className="h-screen w-full bg-secondary-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-primary-800 shadow-none
        flex-shrink-0 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 pb-6 border-b border-white/10">
            <div className="flex flex-col space-y-2">
              <img 
                src="/bluetagus-logo-white.svg" 
                alt="Blue Tagus Logo" 
                className="w-40 h-auto"
              />
              <span className="text-accent text-[10px] tracking-[0.25em] uppercase font-sans font-medium">
                Sales Database Intelligence
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="px-4 py-6 space-y-1.5 flex-1 flex flex-col overflow-y-auto">
            {allNavigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl group transition-all duration-300 w-full ${
                    isActive
                      ? 'bg-white/10 text-accent shadow-sm border border-white/5'
                      : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon 
                      className={`h-5 w-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} 
                      strokeWidth={isActive ? 2 : 1.5} 
                    />
                    <span className="font-heading font-light text-[17px] tracking-wide lowercase pt-0.5">
                      {item.name}
                    </span>
                  </div>
                  {item.name === 'Discovery' && discoveryMeta?.counts?.new > 0 && (
                    <span className="bg-accent text-primary-900 text-[10px] font-bold rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110">
                      {discoveryMeta.counts.new > 99 ? '99+' : discoveryMeta.counts.new}
                    </span>
                  )}
                  {item.name === 'Monitoring' && issuesMeta?.total > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110">
                      {issuesMeta.total > 99 ? '99+' : issuesMeta.total}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-6 border-t border-white/10 bg-primary-900/30">
            <div className="flex items-center space-x-4 mb-5">
              <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center text-accent ring-2 ring-accent/30 font-heading">
                {user?.first_name ? user.first_name.charAt(0) : user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-heading font-normal text-white truncate lowercase">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email?.split('@')[0] || 'User'
                  }
                </p>
                <div className="flex items-center mt-0.5">
                  <div className="h-1.5 w-1.5 rounded-full bg-accent mr-2 animate-pulse" />
                  <p className="text-xs font-heading font-light text-primary-300 lowercase">
                    {user?.role}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-transparent text-white/80
                         font-heading font-light text-sm rounded-lg border border-white/20 hover:border-accent hover:text-accent hover:bg-accent/5
                         focus:outline-none transition-all duration-300"
            >
              <LogOut className="h-4 w-4" />
              <span>sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 bg-primary-50">
        {/* Top Header */}
        <header className="bg-white border-b border-secondary-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-secondary-100"
              >
                {sidebarOpen ? (
                  <X className="h-5 w-5 text-secondary-600" />
                ) : (
                  <Menu className="h-5 w-5 text-secondary-600" />
                )}
              </button>
              {generateBreadcrumbs()}
            </div>
            <div className="flex items-center space-x-4">
              {/* User Identity instead of Date */}
              <div className="flex items-center space-x-3 text-sm">
                <span className="font-medium text-secondary-900">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email}
                </span>
                {user?.role && (
                  <>
                    <span className="text-secondary-300">|</span>
                    <span className="text-secondary-500 capitalize bg-secondary-100 px-2 py-0.5 rounded text-xs font-medium">
                      {user.role.replace('_', ' ')}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto relative">
          {children}
          {/* <TagusAgent /> */}
        </div>
      </main>
    </div>
  )
}
